import { apiFetch, ApiError } from './client';

// Engagement-pulse admin client — mirrors internal/api/pulse.go and
// internal/services/pulse (docs/pulse-engagement-plan.md). Campaigns are
// anonymous surveys of members / guardians / leaders; results are always
// threshold-gated, so a cell can arrive with no value ("not enough responses
// yet") and the UI must render that rather than invent a number.

export type PulseRole = 'member' | 'guardian' | 'leader';
// The org-trend overview can also aggregate every audience onto one timeline
// ('all'). This is a read-only trend filter, never a campaign target role.
export type PulseTrendScope = PulseRole | 'all';
export type PulseStatus = 'draft' | 'open' | 'closed';

export interface PulseCampaign {
  id: string;
  targetRole: PulseRole;
  driverSlice?: string;
  status: PulseStatus;
  opensAt?: string;
  closesAt?: string;
  createdAt?: string;
  // Identity-side participation for the run: how many were invited and how many
  // answered. Exact at any size (it counts who, never what) so it needs none of
  // the suppression handling the answer-side aggregates below do. A draft that
  // was never launched has no invites, so both are 0.
  invited: number;
  responded: number;
}

export interface CreatePulseCampaignInput {
  targetRole: PulseRole;
  driverSlice?: string;
  opensAt?: string;
  closesAt?: string;
}

export interface LaunchResult {
  campaignId: string;
  invited: number;
}

// A suppressed cell (below the minimum-response floor) arrives with
// suppressed=true and no mean/score — render "not enough responses yet", never
// a guessed value.
export interface DriverScore {
  driver: string;
  n: number;
  mean?: number;
  suppressed: boolean;
}

export interface ENPSReport {
  n: number;
  score?: number;
  promoters?: number;
  passives?: number;
  detractors?: number;
  suppressed: boolean;
  // eNPS of the previous run of the same role (for the headline trend arrow).
  prior?: number;
}

export interface TeamScore {
  teamId?: string;
  teamLabel: string;
  n: number;
  mean?: number;
  suppressed: boolean;
}

export interface SliceScore {
  dimension: 'gender' | 'age_band';
  value: string;
  n: number;
  units: number;
  mean?: number;
  suppressed: boolean;
}

// A section roll-up cell. For leader runs this is the finest unit shown (never
// per team, §3.6); members/guardians get it alongside the per-team view.
export interface SectionScore {
  sectionId?: string;
  sectionLabel: string;
  n: number;
  mean?: number;
  suppressed: boolean;
}

// One cell of the team × driver heatmap (§6). Suppressed below the floor.
export interface HeatmapCell {
  driver: string;
  n: number;
  mean?: number;
  suppressed: boolean;
}

export interface HeatmapRow {
  unitId?: string;
  unitLabel: string;
  cells: HeatmapCell[];
}

// The heatmap grid. unit is "team" for member/guardian runs, "section" for
// leaders (who never appear per team). drivers are the column order; every row
// carries a cell per driver in the same order, so it renders as a rectangle.
export interface Heatmap {
  unit: 'team' | 'section';
  drivers: string[];
  rows: HeatmapRow[];
}

// Participation is sourced from the identity side (invited vs responded) — the
// one count that's fine to show unrounded. Note the capitalised wire keys
// (the backend type carries no json tags).
export interface Participation {
  Invited: number;
  Responded: number;
}

// Per-team invited-vs-responded counts (identity side — safe to show unrounded,
// like the overall rate). Powers the "which teams still need a nudge?" view.
export interface TeamParticipation {
  teamId?: string;
  teamLabel: string;
  invited: number;
  responded: number;
}

export interface CampaignReport {
  campaignId: string;
  role: PulseRole;
  participation: Participation;
  participationByTeam: TeamParticipation[];
  drivers: DriverScore[];
  enps: ENPSReport;
  teams: TeamScore[];
  sections: SectionScore[];
  slices: SliceScore[];
  heatmap: Heatmap;
}

// One run's gated headline metrics on the time series (the per-org "how have we
// done over time" comparison). enps/driver mean are absent below the response
// floor — render those points as gaps, never an invented number.
export interface TrendPoint {
  campaignId: string;
  at: string; // RFC3339 — the run's close (or creation) time
  n: number;
  enps?: number;
  drivers: DriverScore[];
}

// A role's own metrics across its recent runs, oldest → newest. Self-over-time
// only — this is NOT a cross-org comparison.
export interface CampaignTrend {
  role: PulseTrendScope;
  points: TrendPoint[];
}

export async function getPulseTrend(
  token: string | null,
  slug: string,
  campaignId: string,
): Promise<CampaignTrend> {
  return apiFetch(
    `${base(slug)}/campaigns/${encodeURIComponent(campaignId)}/trend`,
    { method: 'GET', token },
  );
}

// getPulseRoleTrend returns the org-wide trend for a role across all its runs
// (the results-page overview), not tied to a single campaign.
export async function getPulseRoleTrend(
  token: string | null,
  slug: string,
  role: PulseTrendScope,
): Promise<CampaignTrend> {
  return apiFetch(
    `${base(slug)}/trend?role=${encodeURIComponent(role)}`,
    { method: 'GET', token },
  );
}

// One metric's cross-club peer averages. A cohort value is absent when fewer
// than `floor` OTHER clubs back it (render "not enough clubs yet") — the club
// count is only populated at/above the floor, so a thin cohort never leaks how
// few clubs there are.
export interface BenchmarkCohort {
  metric: string;
  all?: number;
  allClubs: number;
  sport?: number;
  sportClubs: number;
}

// Cross-club comparison for a run: per-metric averages across all clubs and
// same-sport clubs, each gated by the cohort floor. The club's own numbers are
// NOT here (pair with the report). enabled=false → the platform kill-switch is
// off; render nothing.
export interface BenchmarkComparison {
  enabled: boolean;
  role: PulseRole;
  sport: string;
  floor: number;
  cohorts: BenchmarkCohort[];
}

export async function getPulseBenchmark(
  token: string | null,
  slug: string,
  campaignId: string,
): Promise<BenchmarkComparison> {
  return apiFetch(
    `${base(slug)}/campaigns/${encodeURIComponent(campaignId)}/benchmark`,
    { method: 'GET', token },
  );
}

// The AI theme summary of a run's free-text comments (docs/…§7). Aggregate
// strings only — never a verbatim. Absent until an admin generates it.
export interface PulseSummary {
  themes: string[];
  risks: string[];
  actions: string[];
  commentCount: number;
  modelId?: string;
  generatedAt: string;
}

// Anonymous follow-up threads. The admin sees the anonymous comment + the
// conversation and can reply; they never learn who wrote it.
export interface PulseThreadMessage {
  sender: 'club' | 'respondent';
  body: string;
  createdAt: string;
}

export interface PulseThreadSummary {
  threadId: string;
  comments: string[];
  messageCount: number;
  lastSender?: string;
  lastAt: string;
  updatedAt: string;
}

export interface PulseThreadView {
  comments: string[];
  messages: PulseThreadMessage[];
}

const base = (slug: string) =>
  `/api/v1/organizations/${encodeURIComponent(slug)}/pulse`;

export async function listPulseThreads(
  token: string | null,
  slug: string,
  campaignId: string,
): Promise<{ threads: PulseThreadSummary[] }> {
  return apiFetch(
    `${base(slug)}/campaigns/${encodeURIComponent(campaignId)}/threads`,
    { method: 'GET', token },
  );
}

export async function getPulseThread(
  token: string | null,
  slug: string,
  threadId: string,
): Promise<PulseThreadView> {
  return apiFetch(`${base(slug)}/threads/${encodeURIComponent(threadId)}`, {
    method: 'GET',
    token,
  });
}

export async function replyPulseThread(
  token: string | null,
  slug: string,
  threadId: string,
  body: string,
): Promise<void> {
  await apiFetch(`${base(slug)}/threads/${encodeURIComponent(threadId)}/reply`, {
    method: 'POST',
    token,
    body: { body },
  });
}

export async function listPulseCampaigns(
  token: string | null,
  slug: string,
): Promise<{ campaigns: PulseCampaign[] }> {
  return apiFetch(`${base(slug)}/campaigns`, { method: 'GET', token });
}

// Fatigue is the advisory over-surveying signal for a role (§5): recentlySurveyed
// is true when a same-role run was launched within windowDays.
export interface Fatigue {
  recentlySurveyed: boolean;
  lastLaunchedAt?: string;
  windowDays: number;
}

export async function getPulseFatigue(
  token: string | null,
  slug: string,
  role: PulseRole,
): Promise<Fatigue> {
  return apiFetch(
    `${base(slug)}/fatigue?role=${encodeURIComponent(role)}`,
    { method: 'GET', token },
  );
}

export async function createPulseCampaign(
  token: string | null,
  slug: string,
  input: CreatePulseCampaignInput,
): Promise<PulseCampaign> {
  return apiFetch(`${base(slug)}/campaigns`, {
    method: 'POST',
    token,
    body: input,
  });
}

export async function launchPulseCampaign(
  token: string | null,
  slug: string,
  campaignId: string,
): Promise<LaunchResult> {
  return apiFetch(
    `${base(slug)}/campaigns/${encodeURIComponent(campaignId)}/launch`,
    { method: 'POST', token },
  );
}

export async function remindPulseCampaign(
  token: string | null,
  slug: string,
  campaignId: string,
): Promise<{ status: string; pending: number }> {
  return apiFetch(
    `${base(slug)}/campaigns/${encodeURIComponent(campaignId)}/remind`,
    { method: 'POST', token },
  );
}

export async function getPulseReport(
  token: string | null,
  slug: string,
  campaignId: string,
): Promise<CampaignReport> {
  return apiFetch(
    `${base(slug)}/campaigns/${encodeURIComponent(campaignId)}/report`,
    { method: 'GET', token },
  );
}

// ── Recurring auto-send schedules ────────────────────────────────────────────

export interface PulseSchedule {
  id: string;
  targetRole: PulseRole;
  intervalDays: number;
  windowDays: number;
  nextRunAt: string;
  enabled: boolean;
  skippedRuns: string[];
  lastRunAt?: string;
  lastCampaignId?: string;
  createdAt?: string;
}

export interface CreatePulseScheduleInput {
  targetRole: PulseRole;
  intervalDays?: number;
  windowDays?: number;
  firstRunAt?: string;
}

export interface UpdatePulseScheduleInput {
  intervalDays?: number;
  windowDays?: number;
  enabled?: boolean;
  nextRunAt?: string;
  skippedRuns?: string[];
}

export async function listPulseSchedules(
  token: string | null,
  slug: string,
): Promise<{ schedules: PulseSchedule[] }> {
  return apiFetch(`${base(slug)}/schedules`, { method: 'GET', token });
}

export async function createPulseSchedule(
  token: string | null,
  slug: string,
  input: CreatePulseScheduleInput,
): Promise<PulseSchedule> {
  return apiFetch(`${base(slug)}/schedules`, { method: 'POST', token, body: input });
}

export async function updatePulseSchedule(
  token: string | null,
  slug: string,
  id: string,
  input: UpdatePulseScheduleInput,
): Promise<PulseSchedule> {
  return apiFetch(`${base(slug)}/schedules/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    token,
    body: input,
  });
}

export async function deletePulseSchedule(
  token: string | null,
  slug: string,
  id: string,
): Promise<void> {
  await apiFetch(`${base(slug)}/schedules/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    token,
  });
}

export async function skipPulseSchedule(
  token: string | null,
  slug: string,
  id: string,
): Promise<PulseSchedule> {
  return apiFetch(`${base(slug)}/schedules/${encodeURIComponent(id)}/skip`, {
    method: 'POST',
    token,
  });
}

// ── AI theme summary ─────────────────────────────────────────────────────────

// getPulseSummary returns the run's AI theme summary, or null when none has been
// generated yet (the backend 404s — a normal "not made yet" state, not an error).
export async function getPulseSummary(
  token: string | null,
  slug: string,
  campaignId: string,
): Promise<PulseSummary | null> {
  try {
    return await apiFetch<PulseSummary>(
      `${base(slug)}/campaigns/${encodeURIComponent(campaignId)}/summary`,
      { method: 'GET', token },
    );
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

// summarizePulseCampaign kicks off the async summarisation job. 202 → queued;
// the result lands via getPulseSummary once the background job finishes (the
// bell also notifies). A 409 means it can't run yet (AI unavailable, or too few
// comments to summarise safely) — the caller reads err.code to tell which.
export async function summarizePulseCampaign(
  token: string | null,
  slug: string,
  campaignId: string,
): Promise<{ status: string; comments: number }> {
  return apiFetch(
    `${base(slug)}/campaigns/${encodeURIComponent(campaignId)}/summarize`,
    { method: 'POST', token },
  );
}
